import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const DISMISSED_KEY = "voycalc-install-prompt-dismissed";

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;
}

export default function PwaInstallPrompt() {
  const { pathname } = useLocation();
  // Initialise from window cache so the prompt survives component remounts
  const [installPrompt, setInstallPrompt] = useState(
    () => window.__pwaInstallPrompt || null
  );
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return window.sessionStorage.getItem(DISMISSED_KEY) === "true";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      // Persist on window so it survives remounts / HMR
      window.__pwaInstallPrompt = event;
      setInstallPrompt(event);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  // Delay visibility by a tick so the prompt fades in instead of flashing
  const isAllowedPage = ["/login", "/resetPassword", "/welcome"].includes(pathname);
  const shouldShow = isAllowedPage && installPrompt && !dismissed && !isStandalone();

  useEffect(() => {
    if (shouldShow) {
      // Small delay prevents a visible mount-flash when the component remounts
      const id = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(id);
    }
    setVisible(false);
  }, [shouldShow]);

  const install = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    setInstallPrompt(null);
    window.__pwaInstallPrompt = null;
    if (choice?.outcome === "accepted") setDismissed(true);
  };

  const dismiss = () => {
    setDismissed(true);
    try {
      window.sessionStorage.setItem(DISMISSED_KEY, "true");
    } catch {
      // The install prompt still works when browser storage is unavailable.
    }
  };

  if (!shouldShow) return null;

  return (
    <aside
      aria-label="Install VoyCalc"
      style={{
        position: "fixed",
        right: 16,
        bottom: 16,
        zIndex: 1500,
        width: "min(340px, calc(100vw - 32px))",
        padding: 14,
        border: "1px solid #cbd5e1",
        borderRadius: 12,
        background: "#ffffff",
        boxShadow: "0 8px 28px rgba(15, 23, 42, 0.18)",
        color: "#1e293b",
        fontFamily: "Roboto, sans-serif",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(12px)",
        transition: "opacity 0.3s ease, transform 0.3s ease",
      }}
    >
      <button
        type="button"
        aria-label="Dismiss install prompt"
        onClick={dismiss}
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          border: 0,
          background: "transparent",
          color: "#64748b",
          cursor: "pointer",
          fontSize: 18,
          lineHeight: 1,
        }}
      >
        ×
      </button>
      <div style={{ display: "flex", alignItems: "center", gap: 10, paddingRight: 18 }}>
        <img src="/favicon.ico" alt="" width="34" height="34" />
        <div>
          <strong style={{ display: "block", fontSize: 14 }}>Install VoyCalc</strong>
          <span style={{ display: "block", marginTop: 3, color: "#64748b", fontSize: 12 }}>
            Add the app to your desktop for quick access.
          </span>
        </div>
      </div>
      <button
        type="button"
        onClick={install}
        style={{
          width: "100%",
          marginTop: 12,
          padding: "9px 12px",
          border: 0,
          borderRadius: 7,
          background: "#313C4C",
          color: "#ffffff",
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        Install
      </button>
    </aside>
  );
}
